import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Rss } from 'lucide-react';
import { getBlogPostBySlug, getBlogPosts } from '../services/api';
import BlockRenderer from '../components/blog/BlockRenderer';
import { formatDate, readTime, coverFallback } from '../components/blog/blogUtils';

const styles = `
.bp-wrap { position: relative; background: #020617; min-height: 100vh; }
.bp-hero-grid { position: absolute; inset: 0; height: 620px; background-image: linear-gradient(rgba(30,41,59,0.2) 1px, transparent 1px), linear-gradient(90deg, rgba(30,41,59,0.2) 1px, transparent 1px); background-size: 24px 24px; -webkit-mask-image: linear-gradient(to bottom, #000, transparent); mask-image: linear-gradient(to bottom, #000, transparent); z-index: 0; pointer-events: none; }
.bp-container { position: relative; z-index: 1; max-width: min(100rem, calc(100% - clamp(1.5rem, 5vw, 6rem))); margin: 0 auto; padding: 3rem clamp(1.25rem, 5vw, 4rem) 5rem; }
.bp-layout { display: grid; grid-template-columns: 1fr; gap: 3rem; }
@media (min-width: 1024px) { .bp-layout { grid-template-columns: 2fr 1fr; gap: 3.5rem; } }
.bp-back { display: inline-flex; align-items: center; gap: 0.5rem; color: #60a5fa; font-size: 0.9rem; text-decoration: none; margin-bottom: 3rem; }
.bp-back svg { transition: transform 0.2s; }
.bp-back:hover svg { transform: translateX(-4px); }
.bp-cat { display: inline-block; padding: 0.3rem 0.85rem; border-radius: 9999px; background: rgba(59,130,246,0.1); color: #3b82f6; font-size: 0.7rem; font-weight: 700; letter-spacing: 0.12em; text-transform: uppercase; margin-bottom: 1.25rem; }
.bp-datemeta { color: #94a3b8; font-size: 0.8rem; margin-bottom: 1rem; }
.bp-title { font-size: clamp(2.1rem, 5vw, 3.6rem); font-weight: 800; color: #f1f5f9; letter-spacing: -0.025em; line-height: 1.1; margin: 0 0 1.5rem; text-wrap: balance; }
.bp-lead { font-size: 1.2rem; color: #94a3b8; line-height: 1.7; margin: 0 0 2.25rem; }
.bp-author { display: flex; align-items: center; gap: 1rem; padding-bottom: 2.5rem; border-bottom: 1px solid rgba(66,71,84,0.2); }
.bp-avatar { width: 3rem; height: 3rem; border-radius: 50%; background: rgba(59,130,246,0.2); color: #60a5fa; display: flex; align-items: center; justify-content: center; font-weight: 700; border: 1px solid rgba(66,71,84,0.3); flex-shrink: 0; }
.bp-author .name { font-weight: 700; color: #f1f5f9; }
.bp-author .role { color: #94a3b8; font-size: 0.85rem; }
.bp-cover { width: 100%; border-radius: 0.75rem; overflow: hidden; margin: 2.5rem 0 3rem; box-shadow: 0 25px 60px -12px rgba(0,0,0,0.6); }
.bp-cover img, .bp-cover .ph { width: 100%; aspect-ratio: 16 / 9; object-fit: cover; display: block; }
.bp-tags { display: flex; flex-wrap: wrap; gap: 0.75rem; margin-top: 3rem; padding-top: 2rem; border-top: 1px solid rgba(66,71,84,0.2); }
.bp-chip { padding: 0.4rem 1rem; border-radius: 9999px; background: #32353c; color: #93c5fd; font-size: 0.75rem; font-weight: 700; }
.bp-side-inner { position: sticky; top: 6rem; display: flex; flex-direction: column; gap: 2.5rem; }
.bp-side-h { color: #f1f5f9; font-weight: 700; font-size: 1.05rem; margin: 0 0 1.5rem; display: flex; align-items: center; gap: 0.5rem; }
.bp-side-h svg { color: #3b82f6; }
.bp-recent { display: flex; flex-direction: column; gap: 2rem; }
.bp-recent-item { display: block; text-decoration: none; }
.bp-recent-cat { font-size: 0.62rem; font-weight: 700; color: #60a5fa; letter-spacing: 0.12em; text-transform: uppercase; margin-bottom: 0.5rem; }
.bp-recent-item h4 { color: #e1e2ec; font-weight: 600; font-size: 0.95rem; margin: 0 0 0.5rem; line-height: 1.4; transition: color 0.2s; }
.bp-recent-item:hover h4 { color: #60a5fa; }
.bp-recent-meta { color: #94a3b8; font-size: 0.75rem; display: flex; align-items: center; gap: 0.5rem; }
.bp-recent-meta .dot { width: 3px; height: 3px; border-radius: 50%; background: #475569; }
.bp-side-side { border-left: 1px solid rgba(66,71,84,0.2); padding-left: 2rem; }
@media (max-width: 1023px) { .bp-side-side { border-left: none; padding-left: 0; } }
.bp-notfound { position: relative; z-index: 1; max-width: 40rem; margin: 0 auto; padding: 7rem 1.5rem; text-align: center; }
.bp-notfound h1 { font-size: 2rem; color: #f1f5f9; margin: 0 0 1rem; }
.bp-notfound p { color: #94a3b8; margin: 0 0 2rem; }
.bp-btn { display: inline-flex; align-items: center; gap: 0.5rem; background: linear-gradient(to bottom, #1d4ed8, #3b82f6); color: #fff; padding: 0.7rem 1.4rem; border-radius: 0.6rem; font-weight: 700; text-decoration: none; }
`;

const initials = (name = '') => name.split(' ').map((w) => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase() || 'C';

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(undefined); // undefined = loading, null = not found
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    window.scrollTo(0, 0);
    setPost(undefined);
    getBlogPostBySlug(slug).then((data) => {
      setPost(data || null);
      if (data) document.title = `${data.title} | Codinity Blog`;
    });
    getBlogPosts().then((all) => setRecent(all.filter((p) => p.slug !== slug).slice(0, 3)));
  }, [slug]);

  if (post === undefined) {
    return (
      <div className="bp-wrap"><style>{styles}</style>
        <div className="bp-container"><div style={{ height: '400px', borderRadius: '1rem', background: '#0a0f1e' }} /></div>
      </div>
    );
  }

  if (post === null) {
    return (
      <div className="bp-wrap"><style>{styles}</style>
        <div className="bp-notfound">
          <h1>Article not found</h1>
          <p>This post may have been unpublished or the link is incorrect.</p>
          <Link to="/blog" className="bp-btn"><ArrowLeft size={16} /> Back to the blog</Link>
        </div>
      </div>
    );
  }

  const dateLabel = formatDate(post.publishedAt || post.createdAt);

  return (
    <div className="bp-wrap">
      <style>{styles}</style>
      <div className="bp-hero-grid" aria-hidden="true" />
      <div className="bp-container">
        <div className="bp-layout">
          {/* Main article */}
          <article>
            <Link to="/blog" className="bp-back"><ArrowLeft size={16} /> All articles</Link>

            <header>
              {post.category && <span className="bp-cat">{post.category}</span>}
              <div className="bp-datemeta">{dateLabel}{dateLabel && ' · '}{readTime(post)} min read</div>
              <h1 className="bp-title">{post.title}</h1>
              {post.excerpt && <p className="bp-lead">{post.excerpt}</p>}
              <div className="bp-author">
                <div className="bp-avatar">{initials(post.author)}</div>
                <div>
                  <div className="name">{post.author || 'Codinity'}</div>
                  <div className="role">Codinity Engineering</div>
                </div>
              </div>
            </header>

            {post.coverImageUrl ? (
              <div className="bp-cover"><img src={post.coverImageUrl} alt={post.coverImageAlt || post.title} /></div>
            ) : (
              <div className="bp-cover"><div className="ph" style={{ background: coverFallback(post.slug) }} /></div>
            )}

            <div style={{ marginTop: '0.5rem' }}>
              <BlockRenderer blocks={post.blocks} />
            </div>

            {Array.isArray(post.tags) && post.tags.length > 0 && (
              <div className="bp-tags">
                {post.tags.map((t) => <span key={t} className="bp-chip">#{t}</span>)}
              </div>
            )}
          </article>

          {/* Sidebar */}
          <aside>
            <div className="bp-side-inner bp-side-side">
              <div>
                <h3 className="bp-side-h"><Rss size={18} /> Recent Posts</h3>
                {recent.length === 0 ? (
                  <p style={{ color: '#64748b', fontSize: '0.85rem' }}>No other posts yet.</p>
                ) : (
                  <div className="bp-recent">
                    {recent.map((p) => (
                      <Link to={`/blog/${p.slug}`} className="bp-recent-item" key={p._id}>
                        {p.category && <div className="bp-recent-cat">{p.category}</div>}
                        <h4>{p.title}</h4>
                        <div className="bp-recent-meta">
                          <span>{formatDate(p.publishedAt || p.createdAt)}</span>
                          {p.readingMinutes && <><span className="dot" /><span>{p.readingMinutes} min read</span></>}
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
